import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// components
import Header from 'components/UserSelector/Header';
import Footer from 'components/UserSelector/Footer';
import ExternalAccounts from 'components/UserSelector/External';
import InternalAccounts from 'components/UserSelector/Internal';
import Modal from 'components/Modal';
import AccountStatBar from 'components/UserSelector/AccountStatBar';
import AlertModal from 'components/AlertModal/AlertModal';

// actions
import {
  fetchExternalAssignments,
  fetchInternalAssignments,
  resetAccountState,
  fetchInternalNextPage,
  switchAccount,
} from 'actions/account';
import {
  fetchInternalSalesConsultants,
  salesConsultantReset,
} from 'actions/salesConsultant';
import {
  fetchOpcos,
  resetOpcoData,
} from 'actions/opco';
import { resetSyscoProductsState } from 'actions/syscoProducts';
import { resetGlobalPricingState } from 'actions/globalPrice';
import { getRecentDesignsAsync } from "actions/recentDesigns";

// constants & utils
import { deriveRoleInfo } from 'selectors/user';
import AssignedAccount from 'records/AssignedAccount';
import { availableWorkflows } from 'constants/workflows';
import { withRouter } from 'react-router/es';
import { routeCodes } from 'constants/routes';
import Workflow from 'records/Workflow';
import { getCurrentUserBrand } from 'selectors/user-brands';
import { genericErrorMsg } from 'constants/alert';
import { userSelectedInitialState as initialState } from 'constants/account';

@connect(state => {
  const account = state.account;
  const salesConsultant = state.salesConsultant;
  const opcos = state.opco;

  return {
    user: state.user.get('data'),
    accounts: account.get('data'),
    roleInfo: deriveRoleInfo(state),
    meta: {
      count: account.get('count'),
      offset: account.get('nextOffset'),
      territories: account.get('nextTerritory'),
      accLoading: account.get('loading'),
      scLoading: salesConsultant.get('loading'),
      pageLoading: account.get('pageLoading'),
      hasMore: account.get('hasMore'),
      opcoLoading: opcos.get('loading'),
      errorMessage: opcos.get('errorMessage') || account.get('errorMessage') || salesConsultant.get('errorMessage'),
      hasInactive: account.get('hasInactive'),
    },
    salesConsultants: salesConsultant.get('data'),
    opcos: opcos.get('data'),
    currentBrand: getCurrentUserBrand(state),
  }
})
class UserSelector extends Component {

  static propTypes = {
    isOpen: PropTypes.bool,
    changeVisibility: PropTypes.func,
    roleInfo: PropTypes.shape({
      isHierarchical: PropTypes.bool,
      isInternal: PropTypes.bool,
      changeAccAllowed: PropTypes.bool,
    }),
    meta: PropTypes.shape({
      count: PropTypes.number,
      offset: PropTypes.number,
      territories: PropTypes.string,
      accLoading: PropTypes.bool,
      scLoading: PropTypes.bool,
      pageLoading: PropTypes.bool,
      hasMore: PropTypes.bool,
      opcoLoading: PropTypes.bool,
      hasInactive: PropTypes.bool,
    }),
    user: PropTypes.object,
    accounts: PropTypes.object,
    salesConsultants: PropTypes.object,
    opcos: PropTypes.object,
    router: PropTypes.object,
    brandName: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      ...initialState,
    };
  }

  onChangeAccount = () => {
    const { dispatch, router, accounts } = this.props;
    const {
      selectedAccount: { accountId },
    } = this.state;
    const selectedAccount = AssignedAccount.findByAccountId(accounts, accountId);

    if (selectedAccount.get('accountId')) {
      const switchUser = {
        opcoId: selectedAccount.get('opcoId'),
        customerId: selectedAccount.get('customerId'),
        name: selectedAccount.get('name'),
        address: selectedAccount.get('address'),
        postalCode: selectedAccount.get('postalCode'),
        street: selectedAccount.get('street'),
        city: selectedAccount.get('city'),
        state: selectedAccount.get('state'),
      };

      dispatch(resetSyscoProductsState());
      dispatch(resetGlobalPricingState());

      dispatch(switchAccount(switchUser))
        .then((workflows) => {
          const workflow = Workflow.getWorkflowByName(workflows, availableWorkflows.ONBOARDING);
          if (workflow) {
            const route = Workflow.getCurrentWorkflowRoute(workflow);
            router.push(route);
          } else {
            router.push(routeCodes.DASHBOARD);
          }
          
          dispatch(getRecentDesignsAsync());
          this.onCloseModal();
        });
    }
  }

  /**
   * On account item is selected
   * @param {String} accountId selected account Id
   */
  onSelectAccount = accountId => {
    const { accounts } = this.props;
    const selectedAccount = AssignedAccount.findByAccountId(accounts, accountId);

    if (selectedAccount && selectedAccount.get('accountId') && selectedAccount.get('accountId') !== this.state.selectedAccount.accountId) {
      this.setSelectedAccount({
        opcoId: selectedAccount.get('opcoId'),
        customerId: selectedAccount.get('customerId'),
        accountType: selectedAccount.get('accountType'),
        accountId,
        name: selectedAccount.get('name'),
      });
    }
  }

  /**
   * Update state on account select
   * @param {Object} param0 selected account props
   */
  setSelectedAccount = ({ opcoId, customerId, accountId, accountType = null, name = null }) => {
    this.setState({
      selectedAccount: {
        opcoId,
        customerId,
        accountType,
        accountId,
        name,
      }
    });
  }

  /**
   * Fetch external account assignments
   * @param {Boolean} includeInactives should include inactive accounts with the response
   */
  fetchExternalAccounts = includeInactives => {
    this.props.dispatch(fetchExternalAssignments(includeInactives));
  }

  /**
   * Fetch internal account assignments
   * @param {String} opcoId opcoId
   * @param {String} adId federation id
   * @param {String} searchTerm search term key
   * @param {Boolean} includeInactives should include inactive accounts with the response
   */
  fetchInternalAccounts = (opcoId, adId = null, searchTerm = null, includeInactives = false, detailed = false, toChangeAccount = true) => {
    let formattedSearchTerm = null;
    if (searchTerm) {
      formattedSearchTerm = searchTerm.replaceAll(/'/g, "\\'");
    }

    return this.props.dispatch(fetchInternalAssignments({
      opcoId: opcoId,
      adId: adId,
      customerKey: formattedSearchTerm,
      includeInactives: includeInactives,
      detailed: detailed,
      toChangeAccount: toChangeAccount
    }));
  }

  /**
   * fetch sales consultants
   */
  fetchSalesConsultants = (opcoId, adId = null) => {
    this.props.dispatch(fetchInternalSalesConsultants(opcoId, adId));
  }

  /**
   * fetch next internal account page
   */
  fetchNextInternalAccountPage = (opcoId, offset, territories = null, adId = null, searchTerm = null) =>
    this.props.dispatch(fetchInternalNextPage({
      opcoId: opcoId,
      adId: adId,
      customerKey: searchTerm,
      offset: offset,
      territories: territories
    }));

  /**
   * fetch opcos from db
   */
  fetchOpcos = () => this.props.dispatch(fetchOpcos());

  /**
   * On modal close
   */
  onCloseModal = () => {
    const {
      changeVisibility,
      dispatch,
      user,
    } = this.props;

    const userOpcoId = user.get('opcoId');
    const userCustomerId = user.get('syscoCustomerId');

    this.setState({
      ...initialState,
      selectedAccount: {
        ...initialState.selectedAccount,
        opcoId: userOpcoId,
        customerId: userCustomerId,
        accountId: `${userOpcoId}-${userCustomerId}`,
      },
    });

    changeVisibility(false);
    dispatch(resetOpcoData());
    this.onClearAccounts();
  }

  /**
   * Clear account data and sales consultant data
   */
  onClearAccounts = () => {
    const { dispatch } = this.props;

    dispatch(resetAccountState());
    dispatch(salesConsultantReset());
  }

  /**
   * Returns whether the user is restricted to change account
   * @returns {Boolean} is user restricted to change account
   */
  isChangeAccRestricted = () => {
    const { user } = this.props;
    const { selectedAccount } = this.state;

    return !selectedAccount.accountId ||
      (user.get('opcoId') === selectedAccount.opcoId &&
        user.get('syscoCustomerId') === selectedAccount.customerId);
  }

  /**
   * On changing alert modal
   */
  onToggleAlert = () => this.setState({ errorMessage: null });

  /**
   * On changing accounts state filter 
   */
  onChangeAccountStates = () => this.setState(({ includeInactives }) => ({ includeInactives: !includeInactives }));

  /**
   * Returns relevant account list component
   * @returns Internal/External Account list component
   */
  renderAssignedAccounts = () => {
    const {
      roleInfo: {
        isInternal,
        isHierarchical,
        name: roleName,
      },
      accounts,
      user,
      meta,
      salesConsultants,
      opcos,
    } = this.props;
    const {
      selectedAccount,
      includeInactives,
    } = this.state;

    return isInternal ?
      <InternalAccounts
        opcoId={user.get('opcoId')}
        federationId={user.get('syscoFederatedId')}
        fetchAccounts={this.fetchInternalAccounts}
        onSelectAccount={this.onSelectAccount}
        selectedAccount={selectedAccount}
        renderAccountStatBar={this.renderAccountStatBar}
        accounts={accounts}
        isHierarchical={isHierarchical}
        role={roleName}
        meta={meta}
        salesConsultants={salesConsultants}
        fetchSalesConsultants={this.fetchSalesConsultants}
        fetchNextPage={this.fetchNextInternalAccountPage}
        fetchOpcos={this.fetchOpcos}
        opcos={opcos}
        clearAccounts={this.onClearAccounts}
        includeInactives={includeInactives}
      /> :
      <ExternalAccounts
        accounts={accounts}
        fetchAccounts={this.fetchExternalAccounts}
        onSelectAccount={this.onSelectAccount}
        selectedAccount={selectedAccount}
        renderAccountStatBar={this.renderAccountStatBar}
        meta={meta}
        includeInactives={includeInactives}
      />;
  }

  /**
   * Render account stat bar
   * @returns react node
   */
  renderAccountStatBar = (count) => {
    const {
      meta: { count: originalCount, accLoading },
      user,
      currentBrand,
    } = this.props;
    const displayCount = count || originalCount;

    return (
      <AccountStatBar
        count={accLoading ? 0 : displayCount}
        selectedAccount={{
          accountId: `${user.get('opcoId')}-${user.get('syscoCustomerId')}`,
          name: currentBrand.get('brandName'),
        }}
      />
    )
  }

  componentDidUpdate(prevProps) {
    const {
      user,
      meta: { errorMessage },
    } = this.props;
    const {
      user: prevUser,
      meta: { errorMessage: prevError },
    } = prevProps;

    const userOpcoId = user.get('opcoId');
    const userCustomerId = user.get('syscoCustomerId');

    if (
      userOpcoId &&
      userCustomerId &&
      userOpcoId !== prevUser.get('opcoId') &&
      userCustomerId !== prevUser.get('syscoCustomerId')
    ) {
      this.setSelectedAccount({
        opcoId: userOpcoId,
        customerId: userCustomerId,
        accountId: `${userOpcoId}-${userCustomerId}`,
      });
    }
    if (errorMessage && errorMessage !== prevError) {
      this.setState({ errorMessage: genericErrorMsg });
    }
  }

  render() {
    const {
      isOpen,
      meta: {
        accLoading,
        scLoading,
        opcoLoading,
        pageLoading,
      }
    } = this.props;
    const {
      errorMessage,
      includeInactives,
    } = this.state;

    return (
      isOpen ?
        <div>
          <Modal
            open={isOpen}
            width={'modal-body-account-change'}
            headerComponent={<Header />}
            contentComponent={this.renderAssignedAccounts()}
            footerComponent={
              <Footer
                onChangeAccount={this.onChangeAccount}
                onCancel={this.onCloseModal}
                selectedAccount={this.state.selectedAccount}
                isDisabled={this.isChangeAccRestricted()}
                includeInactives={includeInactives}
                onChangeAccountStates={this.onChangeAccountStates}
                isLoading={accLoading || scLoading || opcoLoading || pageLoading}
              />}
          />
          <AlertModal
            showAlert={!!errorMessage}
            alertMessage={errorMessage}
            onToggleAlertModal={this.onToggleAlert}
          />
        </div> :
        null
    );
  }
}

export default withRouter(UserSelector);
